import type { ErpModule } from '@/api/platform.api';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import type { NavItem } from '@/components/layout/Sidebar';

import {
  moduleDefaultApis,
  moduleDefaultPermissionModules,
  moduleDefaultRoutes,
  isPlatformAdminRoute,
} from '@/config/moduleDefaults';
import { getModuleRelated } from '@/config/moduleRelated';

export type ModuleEntry = {
  slug: string;
  aliases?: string[];
  erpModule: ErpModule;
  homePath: string;
  loginPath: string;
  registerPath: string;
  /** Primary module UI routes (prefix match). */
  routePrefixes: string[];
  /** Supporting routes within this module workspace (prefix match). */
  sharedRoutePrefixes: string[];
  apiPrefixes: string[];
  sharedApiPrefixes: string[];
  label: string;
  /** Short label for breadcrumbs and compact UI (defaults to `label`). */
  breadcrumbLabel?: string;
};

const AUTH_API = ['/v1/auth'];
const PLATFORM_READ_API = ['/v1/platform/modules', '/v1/platform/features', '/v1/platform/accessible-modules'];
const FILES_API = ['/v1/files', '/files', '/images', '/documents'];
const ORG_PROFILE_API = ['/v1/organizations'];
/** Header notifications, org dashboard, module billing. */
const CROSS_MODULE_API = [
  '/v1/notifications',
  '/v1/dashboard',
  '/v1/work-queue',
  '/v1/platform/billing',
  '/v1/platform/switch-workspace',
  '/v1/subscriptions',
  '/v1/master-data',
  '/v1/session',
  '/v1/companies',
  '/v1/ui',
];

function paths(...items: string[]): string[] {
  return items;
}

function allRoutes(entry: Pick<ModuleEntry, 'routePrefixes' | 'sharedRoutePrefixes'>): string[] {
  return [...entry.routePrefixes, ...entry.sharedRoutePrefixes];
}

function moduleSharedApi(...extras: string[]): string[] {
  return [
    ...AUTH_API,
    ...PLATFORM_READ_API,
    ...FILES_API,
    ...ORG_PROFILE_API,
    ...CROSS_MODULE_API,
    ...moduleDefaultApis(),
    ...extras,
  ];
}

function registerPathFromLogin(loginPath: string): string {
  return loginPath.replace(/\/login$/, '/register');
}

type EntryConfig = {
  slug: string;
  aliases?: string[];
  erpModule: ErpModule;
  label: string;
  breadcrumbLabel?: string;
  homePath: string;
  loginPath: string;
  routePrefixes: string[];
  apiPrefixes: string[];
};

function buildEntry(config: EntryConfig): ModuleEntry {
  const related = getModuleRelated(config.erpModule);
  return {
    ...config,
    registerPath: registerPathFromLogin(config.loginPath),
    sharedRoutePrefixes: moduleDefaultRoutes(config.erpModule, ...related.routes),
    sharedApiPrefixes: moduleSharedApi(...related.apis),
    // permissionModules stored on backend only; frontend uses routes for scope guard
  };
}

const entries: ModuleEntry[] = [
  buildEntry({
    slug: 'pos',
    erpModule: 'POS',
    label: 'Point of Sale',
    breadcrumbLabel: 'POS',
    homePath: '/pos/dashboard',
    loginPath: '/pos/login',
    routePrefixes: paths('/pos'),
    apiPrefixes: paths('/v1/billing', '/v1/pos'),
  }),
  buildEntry({
    slug: 'crm',
    erpModule: 'CRM',
    label: 'CRM',
    homePath: '/crm',
    loginPath: '/crm/login',
    routePrefixes: paths('/crm'),
    apiPrefixes: paths('/v1/crm'),
  }),
  buildEntry({
    slug: 'hr',
    aliases: ['hrms'],
    erpModule: 'HR',
    label: 'Human Resources',
    breadcrumbLabel: 'HR',
    homePath: '/hr',
    loginPath: '/hr/login',
    routePrefixes: paths('/hr'),
    apiPrefixes: paths('/v1/hr'),
  }),
  buildEntry({
    slug: 'desk',
    erpModule: 'DESK',
    label: 'Help Desk',
    homePath: '/desk',
    loginPath: '/desk/login',
    routePrefixes: paths('/desk'),
    apiPrefixes: paths('/v1/desk'),
  }),
  buildEntry({
    slug: 'finance',
    erpModule: 'FINANCE',
    label: 'Finance',
    homePath: '/finance',
    loginPath: '/finance/login',
    routePrefixes: paths('/finance'),
    apiPrefixes: paths('/v1/finance'),
  }),
  buildEntry({
    slug: 'projects',
    erpModule: 'PROJECTS',
    label: 'Projects',
    homePath: '/projects',
    loginPath: '/projects/login',
    routePrefixes: paths('/projects'),
    apiPrefixes: paths('/v1/projects'),
  }),
  buildEntry({
    slug: 'procurement',
    erpModule: 'PROCUREMENT',
    label: 'Procurement',
    homePath: '/procurement',
    loginPath: '/procurement/login',
    routePrefixes: paths('/procurement', '/vendor-portal'),
    apiPrefixes: paths('/v1/procurement'),
  }),
  buildEntry({
    slug: 'warehouse',
    erpModule: 'WAREHOUSE',
    label: 'Warehouse',
    homePath: '/warehouse',
    loginPath: '/warehouse/login',
    routePrefixes: paths('/warehouse'),
    apiPrefixes: paths('/v1/warehouse', '/v1/warehouses'),
  }),
  buildEntry({
    slug: 'social',
    erpModule: 'SOCIAL',
    label: 'Social',
    breadcrumbLabel: 'Social Media',
    homePath: '/social',
    loginPath: '/social/login',
    routePrefixes: paths('/social'),
    apiPrefixes: paths('/v1/social'),
  }),
  buildEntry({
    slug: 'lead-gen',
    erpModule: 'LEAD_GEN',
    label: 'Lead Generation',
    breadcrumbLabel: 'Lead Generation',
    homePath: '/lead-gen',
    loginPath: '/lead-gen/login',
    routePrefixes: paths('/lead-gen'),
    apiPrefixes: paths('/v1/lead-gen'),
  }),
  buildEntry({
    slug: 'marketing',
    erpModule: 'MARKETING',
    label: 'Marketing',
    homePath: '/marketing',
    loginPath: '/marketing/login',
    routePrefixes: paths('/marketing'),
    apiPrefixes: paths('/v1/marketing'),
  }),
];

const bySlug = new Map<string, ModuleEntry>();
const byErpModule = new Map<string, ModuleEntry>();

for (const entry of entries) {
  bySlug.set(entry.slug, entry);
  byErpModule.set(entry.erpModule, entry);
  for (const alias of entry.aliases ?? []) {
    bySlug.set(alias, entry);
  }
}

export const MODULE_ENTRIES = entries;

/** Map server module codes to launcher entries (head modules only — skips INVENTORY, LOYALTY, etc.). */
export function headModuleEntriesFromCodes(codes: string[]): ModuleEntry[] {
  const seen = new Set<string>();
  const result: ModuleEntry[] = [];
  for (const code of codes) {
    const upper = code.toUpperCase();
    if (seen.has(upper)) continue;
    const entry = getModuleEntryByErpModule(upper);
    if (entry) {
      seen.add(upper);
      result.push(entry);
    }
  }
  return result;
}

/** Org-subscribed head modules (excludes platform sub-modules like INVENTORY, LOYALTY). */
export function enabledHeadModuleCodes(enabledModuleCodes: string[]): string[] {
  return headModuleEntriesFromCodes(enabledModuleCodes).map((entry) => entry.erpModule);
}

/** Launcher apps only — excludes platform sub-modules (INVENTORY, LOYALTY, REPORTING, etc.). */
export function enabledHeadModuleEntries(enabledModuleCodes: string[]): ModuleEntry[] {
  return headModuleEntriesFromCodes(enabledModuleCodes);
}

/** Re-export for consumers that need permission module lists (mirrors backend). */
export function getModulePermissionModules(erpModule: ErpModule): string[] {
  const related = getModuleRelated(erpModule);
  return moduleDefaultPermissionModules(...related.permissionModules);
}

export function getModuleEntryBySlug(slug: string): ModuleEntry | undefined {
  return bySlug.get(slug.toLowerCase());
}

export function getModuleEntryByErpModule(erpModule: string): ModuleEntry | undefined {
  return byErpModule.get(erpModule);
}

/** Compact module name for breadcrumbs (e.g. HR, POS, CRM). */
export function getModuleBreadcrumbLabel(erpModule: string | undefined | null): string | undefined {
  if (!erpModule) return undefined;
  const entry = getModuleEntryByErpModule(erpModule);
  if (!entry) return erpModule;
  return entry.breadcrumbLabel ?? entry.label;
}

export function getModuleRegisterPath(erpModule: string): string {
  return getModuleEntryByErpModule(erpModule)?.registerPath ?? MODULE_HUB_PATH;
}

export function getModuleLoginPath(erpModule: string): string {
  return getModuleEntryByErpModule(erpModule)?.loginPath ?? MODULE_HUB_PATH;
}

/** Resolve module from a product sign-in URL (e.g. /pos/login). */
export function getModuleEntryByLoginPath(pathname: string): ModuleEntry | undefined {
  const path = (pathname.split('?')[0] || '').replace(/\/+$/, '') || '/';
  for (const entry of entries) {
    if (entry.loginPath === path) return entry;
    for (const alias of entry.aliases ?? []) {
      if (`/${alias}/login` === path) return entry;
    }
  }
  return undefined;
}

export function getModuleAllowedRoutes(entry: ModuleEntry): string[] {
  return allRoutes(entry);
}

export function isPathAllowedForEntry(pathname: string, entry: ModuleEntry): boolean {
  const allowed = allRoutes(entry);
  return allowed.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function isNavItemAllowedForEntry(to: string, entry: ModuleEntry): boolean {
  const path = to.split('?')[0];
  if (isGlobalWorkspaceRoute(path)) return true;
  if (isPlatformAdminRoute(path)) return true;
  return isPathAllowedForEntry(path, entry);
}

export function filterNavItemsByEntryModule(items: NavItem[], entryModule: ErpModule): NavItem[] {
  const entry = getModuleEntryByErpModule(entryModule);
  if (!entry) return items;
  return items.filter((item) => isNavItemAllowedForEntry(item.to, entry));
}

export function resolveModuleLoginPathFromRoute(pathname: string): string | null {
  // Primary module prefixes only — shared/global allowlists must not force POS login.
  let best: { loginPath: string; score: number } | null = null;
  const path = pathname.split('?')[0] || '';
  for (const entry of entries) {
    for (const prefix of entry.routePrefixes) {
      const matches =
        path === prefix || (prefix !== '/pos' && path.startsWith(`${prefix}/`));
      if (!matches) continue;
      const score = prefix.length;
      if (!best || score > best.score) {
        best = { loginPath: entry.loginPath, score };
      }
    }
  }
  return best?.loginPath ?? null;
}

export function getAllModuleRegisterRoutes(): { path: string; slug: string }[] {
  const routes: { path: string; slug: string }[] = [];
  for (const entry of entries) {
    routes.push({ path: entry.registerPath, slug: entry.slug });
    for (const alias of entry.aliases ?? []) {
      routes.push({ path: `/${alias}/register`, slug: entry.slug });
    }
  }
  return routes;
}

export function getAllModuleLoginRoutes(): { path: string; slug: string }[] {
  const routes: { path: string; slug: string }[] = [];
  for (const entry of entries) {
    routes.push({ path: entry.loginPath, slug: entry.slug });
    for (const alias of entry.aliases ?? []) {
      routes.push({ path: `/${alias}/login`, slug: entry.slug });
    }
  }
  return routes;
}
